import { useState, useMemo } from "react";
import SEO from "@/components/SEO";
import Reveal from "@/components/Reveal";
import ArticleCard from "@/components/ArticleCard";
import FinalCTA from "@/components/home/FinalCTA";
import { ARTICLES, INSIGHT_CATEGORIES } from "@/data/content";

export default function Insights() {
  const [active, setActive] = useState("All");
  const featured = ARTICLES.find((a) => a.featured) || ARTICLES[0];

  const list = useMemo(() => {
    const base = ARTICLES.filter((a) => a.slug !== featured.slug);
    return active === "All" ? base : ARTICLES.filter((a) => a.category === active);
  }, [active, featured.slug]);

  return (
    <>
      <SEO
        title="Insights | All Data Tech"
        description="Ideas, perspectives and practical thinking across AI, data, analytics, cloud and technology from All Data Tech."
        path="/insights"
      />

      {/* Hero */}
      <section className="relative bg-[#F7F8FA] pt-32 pb-14 lg:pt-40 lg:pb-16" data-testid="insights-hero">
        <div className="absolute inset-0 grid-pattern-light opacity-50" aria-hidden="true" />
        <div className="ad-container relative">
          <Reveal><span className="ad-eyebrow">Insights</span></Reveal>
          <Reveal index={1}>
            <h1 className="mt-4 display-lg text-[#111827]">Insights</h1>
          </Reveal>
          <Reveal index={2}>
            <p className="mt-5 max-w-2xl text-base leading-relaxed text-[#4B5563] sm:text-lg">
              Ideas, perspectives and practical thinking across AI, data, analytics and technology.
            </p>
          </Reveal>
        </div>
      </section>

      {/* Featured story (only on All) */}
      {active === "All" && (
        <section className="bg-[#F7F8FA] pb-16">
          <div className="ad-container">
            <Reveal>
              <ArticleCard article={featured} variant="featured" testId="insights-featured-article" />
            </Reveal>
          </div>
        </section>
      )}

      {/* Topic nav + grid */}
      <section className="ad-section bg-white pt-8">
        <div className="ad-container">
          <div className="-mx-1 flex gap-2.5 overflow-x-auto px-1 pb-2" role="tablist" aria-label="Topics">
            {INSIGHT_CATEGORIES.map((cat) => (
              <button
                key={cat}
                role="tab"
                aria-selected={active === cat}
                data-testid={`insight-filter-${cat.toLowerCase().replace(/[^a-z]+/g, "-").replace(/^-|-$/g, "")}`}
                onClick={() => setActive(cat)}
                className={`whitespace-nowrap rounded-full border px-4 py-2 text-sm transition-colors ${
                  active === cat
                    ? "border-[#1677FF] bg-[#1677FF] text-white"
                    : "border-[#111827]/12 text-[#4B5563] hover:border-[#111827]/30 hover:text-[#111827]"
                }`}
              >
                {cat}
              </button>
            ))}
          </div>

          {list.length > 0 ? (
            <div className="mt-10 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
              {list.map((a, i) => (
                <div key={a.slug} className={i === 0 ? "md:col-span-2 lg:col-span-2" : ""}>
                  <ArticleCard article={a} index={i} variant={i === 0 ? "large" : "default"} testId={`insight-card-${i}`} />
                </div>
              ))}
            </div>
          ) : (
            <p className="mt-10 text-[#6B7280]">No articles in this category yet.</p>
          )}

          <p className="mt-12 text-center text-xs text-[#9CA3AF]">
            Articles shown are illustrative examples for layout purposes and are clearly marked.
          </p>
        </div>
      </section>

      <FinalCTA />
    </>
  );
}
